function copyNode( from, to ) {
	to.innerHTML = from.innerHTML;

	for ( let attribute of from.attributes ) {
		to.setAttribute( attribute.name, attribute.value );
	}
}

function resetInput( elem ) {
	if ( elem.type ) {
		switch ( elem.type ) {
			case 'radio':
			case 'checkbox':
				elem.removeAttribute( 'checked' );
				break;
			case 'hidden':
				// no-op
				break;
			default:
				elem.value = '';
				break;
		}
	}
}

function getDelegateTarget( e, selector, root = document.documentElement ) {
	let node = e.target;

	while ( node && node !== root ) {
		if ( node.matches( selector ) ) {
			return node;
		}

		node = node.parentNode;
	}

	return null;
}

export default class AdvancedQuery {
	constructor( node, opts = {} ) {
		this.node = node;
		this.opts = {
			ajax: true,
			facets: '.advanced-query__facets',
			facetsFacet: '.advanced-query__facet',
			facetOpened: 'advanced-query__facet--opened',
			facetsToggle: '.advanced-query__facet-toggle',
			facetFilters: '.advanced-query__facet-filters',
			active: '.advanced-query__active',
			activeFilter: '.advanced-query__active-filter',
			resultsSet: '.advanced-query__result-set',
			results: '.advanced-query__results',
			pagination: '.advanced-query__pagination',
			paginationLink: '.advanced-query__pagination a',
			paginationMore: '.advanced-query__pagination-more',
			openedClass: 'facets--opened',
			opener: '.facets__heading--mobile',
			...opts,
		};

		this.results = this.node.querySelector( this.opts.results );
		this.resultsSet = this.node.querySelector( this.opts.resultsSet );
		this.pagination = this.node.querySelector( this.opts.pagination );

		this.facets = this.node.querySelector( this.opts.facets );
		this.facetsFacets = this.facets ? this.facets.querySelectorAll( this.opts.facetsFacet ) : [];
		this.active = this.node.querySelector( this.opts.active );
		this.opener = this.node.querySelector( this.opts.opener );
		this.opened = this.node.classList.contains( this.opts.openedClass );
		this.wasFocused = null;
		this.page = 1;

		// handle the search submission
		this.node.addEventListener( 'submit', ( e ) => {
			e.preventDefault();
			this.fetch( this.url() );
		} );

		// handle toggling of a facet dropdown
		this.node.addEventListener( 'click', ( e ) => {

			let target = getDelegateTarget( e, this.opts.facetsToggle, this.opts.node );

			if ( target ) {
				const facet = target.closest( this.opts.facetsFacet );

				if ( facet ) {

					facet.classList.toggle( this.opts.facetOpened );

					const filters = facet.querySelector( this.opts.facetFilters );

					if ( filters ) {
						const filtersBox = filters.getBoundingClientRect();
						;
						// make sure the dropdown doesn't go lower than the viewport
						if ( filtersBox.bottom > document.documentElement.clientHeight ) {
							filters.style.maxHeight = `${ filtersBox.height - ( filtersBox.bottom - document.documentElement.clientHeight ) - 5 }px`;
						}
					}
				}
			}
		} );

		// handle "blur" of a facet dropdown by checking outer click (capturing)
		document.body.addEventListener( 'click', ( e ) => {
			let node = e.target;

			while ( node && node.parentNode ) {
				if ( node.matches( this.opts.facetsFacet ) ) {
					document.querySelectorAll( this.opts.facetsFacet ).forEach( ( n ) => {
						if ( n !== node ) {
							n.classList.remove( this.opts.facetOpened );
						}
					} );
					return;
				}

				node = node.parentNode;
			}

			document.querySelectorAll( this.opts.facetsFacet ).forEach( ( n ) => n.classList.remove( this.opts.facetOpened ) );
		} );

		// handle toggling of a filter
		this.node.addEventListener( 'change', ( { target } ) => {
			if ( [ 'checkbox', 'radio' ].includes( target.type ) ) {
				this.page = 1;
				this.wasFocused = `[name="${ target.name }"][value="${ target.value }"]`;
				this.fetch( this.url() );
			}
		} );

		// handle clearing a filter
		this.node.addEventListener( 'click', ( e ) => {
			const node = getDelegateTarget( e, this.opts.activeFilter, this.node );

			if ( node && node.dataset.name && node.dataset.value ) {
				const input = this.node.querySelector( `[name="${ node.dataset.name }"][value="${ node.dataset.value }"]` );

				if ( input ) {
					this.page = 1;
					resetInput( input );
					this.fetch( this.url() );
				}
			}
		} );

		// handle clearing all filters
		this.node.addEventListener( 'reset', ( e ) => {
			for ( let elem of this.node.elements ) {
				resetInput( elem );
			}

			this.page = 1;
			this.fetch( this.url() );
		} );

		// handle pagination
		this.node.addEventListener( 'click', ( e ) => {
			const node = getDelegateTarget( e, this.opts.paginationLink, this.node );

			if ( node ) {
				e.preventDefault();
				this.fetch( e.target.href );
			}
		} );

		// handle load more
		this.node.addEventListener( 'click', ( e ) => {
			const node = getDelegateTarget( e, this.opts.paginationMore, this.node );

			if ( node ) {
				e.preventDefault();
				this.page++;
				this.fetch( this.url(), true );
			}
		} );

		if ( this.opts.ajax ) {
			// handle back button
			window.addEventListener( 'popstate', ( e ) => this.fetch( location.href, false ) );
		}

		if ( this.opener ) {
			// handle facet toggle view (mobile)
			this.opener.addEventListener( 'click', this.toggle.bind( this ) );
		}

		if ( this.facetsFacets.length >= 5 ) {
			this.facets.classList.add( 'advanced-query__facets--stack' );
		}
	}

	url() {
		let { origin, pathname } = location;

		const formData = new FormData( this.node );
		const formSearchParams = new URLSearchParams( formData );
		const urlSearchParams = new URLSearchParams( location.search );

		// remove any fields from the url params that overlap with fields on the form
		Array.from( this.node.elements )
			.map( ( el ) => el.name )
			.filter( ( v ) => v )
			.forEach( ( name ) => {
				urlSearchParams.delete( name );
			} );

		// add form params to the url params
		for ( let param of formSearchParams.keys() ) {
			const values = formSearchParams.getAll( param );

			for ( let value of values ) {
				if ( ! urlSearchParams.has( param, value ) ) {
					urlSearchParams.append( param, value );
				}
			}
		}

		if ( this.page > 1 ) {
			urlSearchParams.set( 'filter[paged]', this.page );
		} else {
			urlSearchParams.delete( 'filter[paged]' );
		}

		// remove empty values
		for ( let [ param, value ] of urlSearchParams.entries() ) {
			if ( ! value ) {
				urlSearchParams.delete( param );
			}
		}

		const hasQueryString = Array.from( urlSearchParams.keys() ).length > 0;

		return `${ origin }${ pathname }${ hasQueryString ? '?' : '' }${ urlSearchParams }`;
	}

	async fetch( url, append = false ) {
		if ( ! this.opts.ajax ) {
			window.location.href = url;
			return;
		}

		this.node.classList.add( 'is-loading' );
		this.node.querySelectorAll( 'fieldset' ).forEach( ( facet ) => ( facet.disabled = true ) );

		return fetch( url )
			.then( ( response ) => {
				if ( ! response.ok ) {
					throw response;
				}

				return response.text();
			} )
			.then( ( html ) => {
				// @see - https://developer.mozilla.org/en-US/docs/Web/API/DOMParser
				const parser = new DOMParser();
				const doc = parser.parseFromString( html, 'text/html' );

				const facets = doc.querySelector( this.opts.facets );
				const results = doc.querySelector( this.opts.results );
				const pagination = doc.querySelector( this.opts.pagination );
				const active = doc.querySelector( this.opts.active );

				if ( append ) {
					if ( this.results ) {
						this.results.insertAdjacentHTML( 'beforeend', results.innerHTML );
					}
				} else {
					if ( facets && this.facets ) {
						copyNode( facets, this.facets );

						if ( this.facets.querySelectorAll( this.opts.facetsFacet ).length >= 5 ) {
							this.facets.classList.add( 'advanced-query__facets--stack' );
						} else {
							this.facets.classList.remove( 'advanced-query__facets--stack' );
						}
					}

					if ( results && this.results ) {
						copyNode( results, this.results );
					}

					if ( active && this.active ) {
						copyNode( active, this.active );
					}
				}

				if ( pagination ) {
					this.pagination.style.display = '';
					copyNode( pagination, this.pagination );
				} else {
					this.pagination.style.display = 'none';
				}

				this.node.dispatchEvent( new CustomEvent( 'advanced-query:reload', { bubbles: true, cancelable: false } ) );

				if ( ! append ) {
					window.history.pushState( {}, '', url );
				}
			} )
			.catch( ( fault ) => {
				console.error( fault );

				if ( fault instanceof Response && 404 === fault.status ) {
					this.results.innerHTML = `No results found for your filters. Please try again.`;
					this.pagination.innerHTML = '';

					return;
				}

				window.location.href = url;
			} )
			.finally( () => {
				this.node.classList.remove( 'is-loading' );
				this.node.querySelectorAll( 'fieldset' ).forEach( ( facet ) => facet.removeAttribute( 'disabled' ) );

				if ( ! append && this.node.getBoundingClientRect().top < 0 ) {
					this.node.scrollIntoView( { behavior: 'smooth' } );
				}
			} );
	}

	toggle() {
		return this.node.classList.contains( this.opts.openedClass ) ? this.close() : this.open();
	}

	open() {
		this.node.classList.add( this.opts.openedClass );
	}

	close() {
		this.node.classList.remove( this.opts.openedClass );
	}
};
